import { classNames, Text } from "@alpacahq/ui/old";

import type { AcatsTransfer } from "~/v2/api/rest/acats";
import { ACATS_ACCOUNT_TYPE_MAP } from "~/v2/constants";
import { getContraBrokerDisplayName } from "../common/util";

/**
 * Helper function to get human-readable account type
 * Note: This function assumes accountType is defined - caller should check for undefined
 */
const getAccountTypeDisplay = (accountType: string): string => {
  return (
    ACATS_ACCOUNT_TYPE_MAP[
      accountType as keyof typeof ACATS_ACCOUNT_TYPE_MAP
    ] || accountType
  );
};

/**
 * Renders transfer direction information (From/To) for ACATS transfers
 * Used in both table displays and detail modals
 */
export const renderTransferDirection = (
  transfer: AcatsTransfer,
  options: {
    direction: "FROM" | "TO";
    textStyles?: {
      brokerName?: string;
      accountInfo?: string;
    };
    className?: string;
    brokerNameMap?: Map<string, string>;
  },
) => {
  const { direction, textStyles, className, brokerNameMap } = options;
  const isIncoming = transfer.direction === "INCOMING";
  const showAlpaca =
    (direction === "FROM" && !isIncoming) || (direction === "TO" && isIncoming);

  const brokerNameStyle = textStyles?.brokerName || "text-sm";
  const accountInfoStyle = textStyles?.accountInfo || "text-sm";

  if (showAlpaca) {
    return (
      <div className={className}>
        <Text className={brokerNameStyle}>Alpaca</Text>
        {transfer.account_type && (
          <Text className={accountInfoStyle}>
            {getAccountTypeDisplay(transfer.account_type)}
          </Text>
        )}
        <Text className={classNames(accountInfoStyle, "font-mono")}>
          {transfer.account_number}
        </Text>
      </div>
    );
  }

  // Get broker name from mapping, fallback to stored name, then to "-"
  // Extract last 4 digits from contra_broker_number to match DTC numbers
  const dtcNumber = transfer.contra_broker_number?.slice(-4);

  const brokerName =
    (dtcNumber && brokerNameMap?.get(dtcNumber)) ||
    transfer.contra_broker_name ||
    "-";

  // Format the broker name for display
  const displayBrokerName =
    brokerName === "-" ? "-" : getContraBrokerDisplayName(brokerName);

  return (
    <div>
      <Text className={brokerNameStyle}>{displayBrokerName}</Text>
      {transfer.account_type && (
        <Text className={accountInfoStyle}>
          {getAccountTypeDisplay(transfer.account_type)}
        </Text>
      )}
      <Text className={classNames(accountInfoStyle, "font-mono")}>
        {transfer.contra_account_number}
      </Text>
    </div>
  );
};
